import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  RefreshCw,
  PackageCheck,
  Search,
  Clock,
  Inbox,
  AlertOctagon,
  Megaphone,
  Wrench,
  MapPin,
  Download,
} from "lucide-react";
import { formatLocalDateTime } from "../lib/constants";
import { toast } from "sonner";
import { downloadCsv } from "../lib/downloadCsv";
import { DeductStockDialog } from "../components/DeductStockDialog";
import ReleasedByMeStrip from "../components/ReleasedByMeStrip";

const REFRESH_MS = 30000;

export default function MyPendingPickups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [q, setQ] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/shipments", {
        params: { prep_status: "Ready for Handover", limit: 500 },
      });
      // Backend filter is generic; further filter on this user's name/email
      const me = JSON.parse(localStorage.getItem("rougee:user") || "null");
      const name = (me?.name || "").trim().toLowerCase();
      const email = (me?.email || "").trim().toLowerCase();
      const mine = data.filter((s) => {
        const cl = (s.Assigned_Crew_Leader || "").toLowerCase();
        if (!cl) return false;
        if (name && cl.includes(name)) return true;
        if (email && cl.includes(email)) return true;
        return false;
      });
      setItems(mine);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const markPickedUp = async (s) => {
    try {
      await api.patch(`/shipments/${s.id}`, { Preparation_Queue_Status: "Completed" });
      toast.success(`Picked up MR ${s.MR_Number}`);
      setItems((curr) => curr.filter((x) => x.id !== s.id));
    } catch {
      toast.error("Update failed");
    }
  };

  // Auto-stock deduction modal — Mark Picked Up = Completed transition
  const [pendingPickup, setPendingPickup] = useState(null); // shipment object

  const requestPickup = (s) => setPendingPickup(s);
  const onDeductResolved = () => {
    if (!pendingPickup) return;
    const s = pendingPickup;
    setPendingPickup(null);
    markPickedUp(s);
  };
  const onDeductCancel = () => setPendingPickup(null);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const me = JSON.parse(localStorage.getItem("rougee:user") || "null");
      const params = {
        prep_status: "Ready for Handover",
        assigned_crew_leader: me?.name || me?.email || "",
      };
      if (q.trim()) params.q = q.trim();
      // Narrow to currently visible (post-client-filter) ids when possible
      const visibleIds = filtered.map((s) => s.id).filter(Boolean);
      if (visibleIds.length > 0 && visibleIds.length !== items.length) {
        params.ids = visibleIds.join(",");
      }
      await downloadCsv({
        url: "/shipments/export",
        params,
        fallbackFilename: `Material_Shipments_${new Date().toISOString().slice(0, 10)}.csv`,
      });
    } catch (e) {
      // downloadCsv toasts; log for dev tools/monitoring.
      console.error("Pickups CSV export failed", e);
    } finally {
      setExporting(false);
    }
  };

  const filtered = items.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      s.MR_Number.toLowerCase().includes(needle) ||
      (s.Contents || "").toLowerCase().includes(needle) ||
      (s.Project_Station || "").toLowerCase().includes(needle) ||
      (s.Storage_Detail || "").toLowerCase().includes(needle) ||
      (s.Minor_Materials || "").toLowerCase().includes(needle) ||
      (s.Equipment_Needed || "").toLowerCase().includes(needle) ||
      (s.Unavailable_Items || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-purple-700 flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Crew Leader · Ready Now
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight">
            My Pending Pickups
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Material that the Yard Runner has staged for you — go pick it up.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={exporting || items.length === 0}
            data-testid="pickups-export-csv-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white disabled:opacity-50"
            title="Export pickups to CSV"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "CSV"}
          </button>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="pickups-refresh-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {lastRefresh && (
        <div className="text-xs text-slate-500 mb-3 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Last refreshed {formatLocalDateTime(lastRefresh.toISOString())} · {filtered.length} item
          {filtered.length === 1 ? "" : "s"}
        </div>
      )}

      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="pickups-search"
          placeholder="Search MR, station, storage, materials…"
          className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white text-base focus:border-slate-900 outline-none"
        />
      </div>

      {/* Tools currently checked out to me — quick-return strip */}
      <ReleasedByMeStrip />

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="pickups-empty">
          <div className="font-display text-xl text-slate-900 uppercase mb-1">
            Nothing pending pickup
          </div>
          <div className="text-sm text-slate-600">
            Once the Yard Runner marks something Ready for Handover and assigns it to you, it'll appear here.
          </div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="pickups-list">
          {filtered.map((s) => (
            <li
              key={s.id}
              data-testid={`pickup-card-${s.MR_Number}`}
              className="rg-card p-4 sm:p-5 border-l-[6px] border-l-purple-700"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Link
                      to={`/shipments/${s.id}`}
                      className="font-mono text-lg font-bold text-slate-900 hover:underline"
                    >
                      {s.MR_Number}
                    </Link>
                    <span className="rg-pill bg-purple-100 text-purple-800 border-2 border-purple-700">
                      Ready for Handover
                    </span>
                  </div>
                  <div className="text-sm text-slate-700 font-bold flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-slate-500" />
                    {s.Project_Station}
                  </div>

                  {s.Storage_Detail ? (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 bg-amber-100 border-2 border-amber-400 px-3 py-1 rounded-sm text-xs font-bold text-amber-900 uppercase tracking-wide"
                      data-testid={`pickup-storage-${s.MR_Number}`}
                    >
                      Pick from {s.Storage_Detail === "Other Storage" && s.Storage_Other ? s.Storage_Other : s.Storage_Detail}
                    </div>
                  ) : s.Current_Location === "Main yard" ? (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 bg-amber-100 border-2 border-amber-400 px-3 py-1 rounded-sm text-xs font-bold text-amber-900 uppercase tracking-wide"
                      data-testid={`pickup-storage-needed-${s.MR_Number}`}
                    >
                      ⚠ Stocking Location Needed
                    </div>
                  ) : null}

                  {s.Requestor_Message && (
                    <div className="mt-2 bg-purple-50 border-l-4 border-purple-700 px-3 py-2 rounded-sm">
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-purple-700 mb-1">
                        <Megaphone className="h-3 w-3" />
                        Crew message
                      </div>
                      <div className="text-sm text-slate-900">{s.Requestor_Message}</div>
                    </div>
                  )}

                  {s.Contents && (
                    <div className="text-sm text-slate-800 mt-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1">
                        Contents
                      </span>
                      {s.Contents}
                    </div>
                  )}

                  {s.Minor_Materials && (
                    <div className="text-sm text-slate-700 mt-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1">
                        Minors
                      </span>
                      {s.Minor_Materials}
                    </div>
                  )}

                  {s.Equipment_Needed && (
                    <div className="mt-2 flex items-start gap-2 bg-slate-100 border-2 border-slate-300 px-3 py-2 rounded-sm">
                      <Wrench strokeWidth={2.5} className="h-4 w-4 text-slate-700 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 text-sm">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700">
                          Equipment
                        </div>
                        <div className="text-slate-900 font-bold">{s.Equipment_Needed}</div>
                      </div>
                    </div>
                  )}

                  {s.Unavailable_Items && (
                    <div
                      className="mt-2 flex items-start gap-2 bg-rose-50 border-2 border-rose-600 px-3 py-2 rounded-sm"
                      data-testid={`pickup-unavailable-${s.MR_Number}`}
                    >
                      <AlertOctagon strokeWidth={2.5} className="h-4 w-4 text-rose-700 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 text-sm">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
                          Unavailable / missing
                        </div>
                        <div className="text-rose-900 font-bold">{s.Unavailable_Items}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => requestPickup(s)}
                data-testid={`pickup-mark-picked-${s.MR_Number}`}
                className="rg-tap-xl w-full bg-purple-700 hover:bg-purple-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-purple-900 min-h-[60px] text-base"
              >
                <PackageCheck strokeWidth={3} className="h-5 w-5" />
                Mark as Picked Up
              </button>
            </li>
          ))}
        </ul>
      )}
      {pendingPickup && (
        <DeductStockDialog
          shipmentId={pendingPickup.id}
          open={true}
          onResolved={onDeductResolved}
          onCancel={onDeductCancel}
        />
      )}
    </div>
  );
}
